import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'modules/ui/form/useForm';
import { InputWithLabel, Loader, SelectWithLabel } from 'modules/ui';
import { FormButtons, FormContainer } from 'modules/ui/form';
import { useAppSelector } from 'modules/hooks/appHooks';
import { LeasingCalculatorCodesSelectors } from 'modules/hooks/codesHooks/redux/leasingCalculatorCodes';
import { UwtType } from 'modules/types/LeasingCalculatorCodes';
import { parseToInteger } from 'modules/utils';
import { hasOnlyEmptyValues } from 'modules/utils/uwtTypesHelper';
import { LeasingCalculatorSharedFormProps } from '../../common/LeasingCalculatorData';
import { ValidationType } from '../../../../../types/ValidationType';
import { useLeasingCalculatorObjectOptions } from '../hooks/useLeasingCalculatorObjectOptions';
import { leasingObjectDependencies } from './helper';

export const LeasingCalculatorObjectData: React.FC<
  LeasingCalculatorSharedFormProps
> = ({ defaultValues, onChange, onNextStep, onPreviousStep }) => {
  const { t } = useTranslation();

  const leasingObjectTypesLoading = useAppSelector(
    LeasingCalculatorCodesSelectors.selectLeasingObjectTypesAndMakesLoading,
  );

  const {
    leasingObjectTypes,
    leasingObjects,
    leasingObjectMakes,
    leasingObjectModels,
    leasingObjectFuels,
    leasingObjectEngines,
    leasingObjectRegistrationYears,
    leasingObjectAverageTotalMileage,
    fetchLeasingObjectDataByMake,
  } = useLeasingCalculatorObjectOptions(defaultValues);

  const { validateFlag, handleOnSubmit, updateFormData, updateSelectData } =
    useForm({
      onChange,
      onNextStep,
      onPreviousStep,
    });

  const updateCurrentAndNext = (
    propertyName: string,
    value: number,
    types: UwtType[],
  ) => {
    updateSelectData(propertyName, value, types);
    clearDependentProperties(propertyName);
  };

  const clearDependentProperties = (propertyName: string) => {
    const currentIdx = leasingObjectDependencies.findIndex(
      (item) => item === propertyName,
    );
    for (let i = currentIdx + 1; i < leasingObjectDependencies.length; i += 1) {
      updateFormData(leasingObjectDependencies[i], undefined);
    }
  };

  const updateAndFetchByMake = (
    propertyName: string,
    value: number,
    types: UwtType[],
  ) => {
    updateCurrentAndNext(propertyName, value, types);
    fetchLeasingObjectDataByMake(value);
  };

  const optionalDropDownsValuesExist =
    (leasingObjectFuels.options.length > 0 &&
      !hasOnlyEmptyValues(leasingObjectFuels.options) &&
      !defaultValues?.leasingObjectFuel) ||
    (leasingObjectEngines.options.length > 0 &&
      !hasOnlyEmptyValues(leasingObjectEngines.options) &&
      !defaultValues?.leasingObjectEngine);

  return (
    <Loader active={leasingObjectTypesLoading}>
      <FormContainer noValidate onSubmit={handleOnSubmit}>
        <SelectWithLabel
          required
          searchable={false}
          label={t('leasingCalculator.objectData.leasingObjectType')}
          options={leasingObjectTypes.options}
          placeholderText={t(
            'leasingCalculator.objectData.leasingObjectTypePlaceholder',
          )}
          defaultValue={defaultValues?.leasingObjectType?.id}
          onChange={(value) =>
            updateCurrentAndNext(
              'leasingObjectType',
              value,
              leasingObjectTypes.uwtTypes,
            )
          }
          validators={[ValidationType.REQUIRED]}
          validate={validateFlag}
        />
        <SelectWithLabel
          required
          searchable={false}
          label={t('leasingCalculator.objectData.leasingObject')}
          disabled={!defaultValues?.leasingObjectType}
          options={leasingObjects.options}
          placeholderText={t(
            'leasingCalculator.objectData.leasingObjectPlaceholder',
          )}
          defaultValue={defaultValues?.leasingObject?.id}
          onChange={(value) =>
            updateSelectData('leasingObject', value, leasingObjects.uwtTypes)
          }
          validators={[ValidationType.REQUIRED]}
          validate={validateFlag}
        />
        <SelectWithLabel
          required
          label={t('leasingCalculator.objectData.leasingObjectMake')}
          disabled={!defaultValues?.leasingObject}
          options={leasingObjectMakes.options}
          placeholderText={t(
            'leasingCalculator.objectData.leasingObjectMakePlaceholder',
          )}
          defaultValue={defaultValues?.leasingObjectMake?.id}
          onChange={(value) =>
            updateAndFetchByMake(
              'leasingObjectMake',
              value,
              leasingObjectMakes.uwtTypes,
            )
          }
          validators={[ValidationType.REQUIRED]}
          validate={validateFlag}
        />
        <SelectWithLabel
          required
          disabled={!defaultValues?.leasingObjectMake}
          label={t('leasingCalculator.objectData.leasingObjectModel')}
          options={leasingObjectModels.options}
          placeholderText={t(
            'leasingCalculator.objectData.leasingObjectModelPlaceholder',
          )}
          defaultValue={defaultValues?.leasingObjectModel?.id}
          onChange={(value) =>
            updateCurrentAndNext(
              'leasingObjectModel',
              value,
              leasingObjectModels.uwtTypes,
            )
          }
          validators={[ValidationType.REQUIRED]}
          validate={validateFlag}
        />
        {leasingObjectFuels.options.length > 0 &&
          !hasOnlyEmptyValues(leasingObjectFuels.options) && (
            <SelectWithLabel
              required
              searchable={false}
              disabled={!defaultValues?.leasingObjectModel}
              label={t('leasingCalculator.objectData.leasingObjectFuel')}
              options={leasingObjectFuels.options}
              placeholderText={t(
                'leasingCalculator.objectData.leasingObjectFuelPlaceholder',
              )}
              defaultValue={defaultValues?.leasingObjectFuel?.id}
              onChange={(value) =>
                updateCurrentAndNext(
                  'leasingObjectFuel',
                  value,
                  leasingObjectFuels.uwtTypes,
                )
              }
              validators={[ValidationType.REQUIRED]}
              validate={validateFlag}
            />
          )}
        {leasingObjectEngines.options.length > 0 &&
          !hasOnlyEmptyValues(leasingObjectEngines.options) && (
            <SelectWithLabel
              required
              searchable={false}
              disabled={!defaultValues?.leasingObjectFuel}
              label={t('leasingCalculator.objectData.leasingObjectEngine')}
              options={leasingObjectEngines.options}
              placeholderText={t(
                'leasingCalculator.objectData.leasingObjectEnginePlaceholder',
              )}
              defaultValue={defaultValues?.leasingObjectEngine?.id}
              onChange={(value) =>
                updateSelectData(
                  'leasingObjectEngine',
                  value,
                  leasingObjectEngines.uwtTypes,
                )
              }
              validators={[ValidationType.REQUIRED]}
              validate={validateFlag}
            />
          )}
        {defaultValues?.leasingObjectType?.name && defaultValues?.leasingObjectType?.name !== 'Nekretnine' &&
            <SelectWithLabel
                required
                searchable={false}
                label={t(
                    'leasingCalculator.objectData.leasingObjectFirstRegistrationYear',
                )}
                options={leasingObjectRegistrationYears.options}
                placeholderText={t(
                    'leasingCalculator.objectData.leasingObjectFirstRegistrationYearPlaceholder',
                )}
                defaultValue={defaultValues?.leasingObjectFirstRegistrationYear?.id}
                onChange={(value) =>
                    updateSelectData(
                        'leasingObjectFirstRegistrationYear',
                        value,
                        leasingObjectRegistrationYears.uwtTypes,
                    )
                }
                validators={[ValidationType.REQUIRED]}
                validate={validateFlag}
            />
        }
        {defaultValues?.leasingObjectType?.name && defaultValues?.leasingObjectType?.name !== 'Nekretnine' &&
            <InputWithLabel
                required
                label={t(
                    'leasingCalculator.objectData.currentNumberOfKilometersTraveledOrWorkingHours',
                )}
                placeholder={t(
                    'leasingCalculator.objectData.currentNumberOfKilometersTraveledOrWorkingHoursPlaceholder',
                )}
                parser={(value) => value && parseToInteger(value)}
                type="integer"
                defaultValue={
                  defaultValues?.currentNumberOfKilometersTraveledOrWorkingHours
                }
                onBlur={(e) =>
                    updateFormData(
                        'currentNumberOfKilometersTraveledOrWorkingHours',
                        e.target.value,
                    )
                }
                validators={[ValidationType.REQUIRED]}
                validate={validateFlag}
            />
        }
        {defaultValues?.leasingObjectType?.name && defaultValues?.leasingObjectType?.name !== 'Nekretnine' &&
            <SelectWithLabel
                required
                searchable={false}
                disabled={
                    !defaultValues?.leasingObjectModel || optionalDropDownsValuesExist
                }
                label={t(
                    'leasingCalculator.objectData.annualNumberOfKilometersPlanned',
                )}
                options={leasingObjectAverageTotalMileage.options}
                placeholderText={t(
                    'leasingCalculator.objectData.annualNumberOfKilometersPlannedPlaceholder',
                )}
                defaultValue={defaultValues?.averageTotalMileage?.id}
                onChange={(value) =>
                    updateSelectData(
                        'averageTotalMileage',
                        value,
                        leasingObjectAverageTotalMileage.uwtTypes,
                    )
                }
                validators={[ValidationType.REQUIRED]}
                validate={validateFlag}
            />
        }
        <FormButtons onPreviousStep={onPreviousStep} />
      </FormContainer>
    </Loader>
  );
};
